import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { getMarketContext } from '@theme/market-context';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    }
    this.checkZeroPrice();
  }

  checkZeroPrice() {
    const priceEl = this.querySelector('.price');
    if (priceEl && (priceEl.textContent.includes('0,00') || priceEl.textContent.includes('0.00'))) {
      const handle = this.dataset.productHandle;
      const market = getMarketContext();
      if (handle && market?.currency?.isoCode && market.rootUrl) {
        const rootUrl = market.rootUrl.replace(/\/?$/, '/');
        fetch(`${rootUrl}products/${encodeURIComponent(handle)}.js`)
          .then((res) => res.json())
          .then((product) => {
            if (product.price > 0) {
              const currency = market.currency.isoCode;
              const locale = market.locale || document.documentElement.lang;
              const formattedPrice = new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: currency,
              }).format(product.price / 100);

              priceEl.textContent = formattedPrice;

              const compareEl = this.querySelector('.compare-at-price');
              if (compareEl && product.compare_at_price > product.price) {
                const formattedCompare = new Intl.NumberFormat(locale, {
                  style: 'currency',
                  currency: currency,
                }).format(product.compare_at_price / 100);
                compareEl.textContent = formattedCompare;
              }
            }
          })
          .catch((err) => console.error('Error fetching price:', err));
      }
    }
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
